import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Typography, CircularProgress, Button } from '@mui/material';

import {
    SelectOne, StepActions, ErrorList, CheckLine, Section, Message, ORANGE, MUTED,
} from '../../PbdqSignup/components/SignupFields.comp';
import {
    RegistryForm, emptyRegistry, DeploymentForm, ChecklistForm, DocumentsForm, NoteField, OrderReview,
} from '../../PbdqSignup/components/SignupForms.comp';
import { TEXT, LEGAL_TERMS_URL } from '../../PbdqSignup/signup.text';
import { saveToken, readToken, clearHandover } from '../../PbdqSignup/signup.session';
import DqtCheckoutDialog from '../../components/DqtCheckoutDialog.comp';
import {
    quoteSignupOrderService,
    startSignupPageApplicationService,
    saveSignupScreeningService,
    submitSignupChecklistService,
    uploadSignupDocumentService,
    submitSignupVerificationService,
    startSignupCardPaymentService,
    reviseSignupOrderService,
    startSignupWirePaymentService,
    uploadSignupWireProofService,
    getSignupPaymentStatusService,
    resendSignupActivationService,
    getSignupApplicationService,
    validateSignupDetailsService,
} from '../../../../services/pbdqSignup.service';

/**
 * SignupApplicationSteps — the DQ Pro V2 sign-up page (/signup): the chat's own steps
 * after its hand-over, as form pages, with the chat's wording and forms (13–14 Oct
 * 2026). No password is asked and no account is created here: each path starts the
 * chat's own application in pbdq_applications, and its account gets a generated
 * password like the chat's (activation email, reset at first sign-in).
 *
 *   From the chat, direct route (handed over after DS-3, with solution + deployment):
 *     DS-4 information → DS-5 order review + terms → payment (card / bank transfer)
 *   From the chat, DQ Pro route (handed over after DP-5, with the solution):
 *     DP-6 information (+ legal entity) → DP-7 screening → DP-8 checklist → DP-9
 *     DP-7 "for our own organization" → DS-1 solution → DS-3 deployment → DS-4 → DS-5
 *     COS Government: notice → DP-6 → documents → submitted
 *   Opened directly (no chat):
 *     information + organisation → submitted as an account request for an admin
 *
 * The information page is where the email is entered (Business email), as in the chat.
 * School or network comes from the organization type ("School" = a single school).
 *
 * It is also where a sign-up application comes back to (14 Oct 2026):
 *   link — /signup?ref=&dqt_checkout= (a card payment's redirect) → the paid page;
 *          /signup?ref=&pay=1&t= (the DQ Pro payment link) → payment
 */

// An agent message (signup.text), laid out as the document lays it out.
const Heading = ({ value }) => (
    <Typography component="div" sx={{ fontSize: '1rem', lineHeight: 1.6, color: 'var(--brand-text, #111827)', mb: 1 }}><Message value={value}/></Typography>
);

const Paragraphs = ({ lines }) => lines.map((l) => (
    <Typography key={l} sx={{ fontSize: '0.92rem', lineHeight: 1.6, mb: 1, color: 'var(--brand-text, #111827)' }}>{l}</Typography>
));

const Done = ({ title, lines }) => (
    <Box>
        <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', mb: 1 }}>{title}</Typography>
        <Paragraphs lines={lines}/>
    </Box>
);

const firstStepOf = (handover) => {
    if (handover?.route === 'direct') return 'DS4';
    if (handover?.route === 'dq_pro') return handover.solution === 'P0_GOV' ? 'GOV_NOTICE' : 'DP6';
    return 'REQUEST';
};

/**
 * @param {function} [onBackFromFirst]  Go back on the first page (back to the chat that
 *   handed over); none when the page was opened directly.
 */
const SignupApplicationSteps = ({ handover, catalog, onBackFromFirst, link = null }) => {
    const history = useHistory();
    const [step, setStep] = useState(() => (link ? 'LOADING' : firstStepOf(handover)));
    const [stack, setStack] = useState([]);
    const [busy, setBusy] = useState(false);
    const [errors, setErrors] = useState([]);

    const [route, setRoute] = useState(handover?.route || 'account');
    const [solution, setSolution] = useState(handover?.solution || '');
    const [deployment, setDeployment] = useState(handover?.deployment || {});
    const [quote, setQuote] = useState(null);
    const [registry, setRegistry] = useState(emptyRegistry);
    const [terms, setTerms] = useState(false);
    const [intendedUse, setIntendedUse] = useState('');
    const [checklist, setChecklist] = useState({});
    const [docNote, setDocNote] = useState('');

    const [app, setApp] = useState(null);               // { reference_no, token, ...publicView }
    const [payment, setPayment] = useState(null);
    const [paymentOpen, setPaymentOpen] = useState(false);

    // The email entered on the information page; arriving by a link, the application's.
    const email = registry.contact.email || app?.contact?.email || '';

    const solutionCode = solution || app?.solution || '';
    const solutionLabel = catalog?.SOLUTIONS?.find((s) => s.value === solutionCode)?.label || app?.solution_label || solutionCode;
    const firstName = registry.contact.first_name || app?.contact?.first_name || '';
    const orgName = registry.organization.name || app?.organization?.name || '';

    // Back from a card payment's redirect, or from the DQ Pro payment link.
    useEffect(() => {
        if (!link) return;
        (async () => {
            const token = link.token || readToken(link.ref);
            if (!token) { setStep('LINK_INVALID'); return; }
            if (link.sessionId) {
                const res = await getSignupPaymentStatusService(link.ref, token, link.sessionId).catch(() => null);
                const view = res?.data?.application;
                if (!view) { setStep('LINK_INVALID'); return; }
                setApp({ ...view, token });
                setStep(['paid', 'fulfilled'].includes(view.status) ? 'PAID' : 'PAYMENT_PROCESSING');
                return;
            }
            const res = await getSignupApplicationService(link.ref, token).catch(() => null);
            const view = res?.data?.application;
            if (!view) { setStep('LINK_INVALID'); return; }
            saveToken(link.ref, token);
            setApp({ ...view, token });
            setStep(view.status === 'awaiting_payment' ? 'PAY' : (['paid', 'fulfilled'].includes(view.status) ? 'PAID' : 'LINK_INVALID'));
        })();
    }, [link]);

    const toHome = () => history.push('/powered-by-dq');
    const toChat = () => history.push('/powered-by-dq/chatbot');

    // ── Navigation ───────────────────────────────────────────────────────────
    const go = (next) => { setErrors([]); setStack((s) => [...s, step]); setStep(next); };
    const goBack = () => {
        setErrors([]);
        if (!stack.length) { onBackFromFirst(); return; }
        setStep(stack[stack.length - 1]);
        setStack((s) => s.slice(0, -1));
    };
    // No Go back on the first page of a sign-up opened directly.
    const back = stack.length || onBackFromFirst ? goBack : undefined;
    // Once an application exists its earlier steps cannot be changed, as in the chat.
    const backIfNoApp = app ? undefined : back;

    const fail = (res, fallback = 'Something went wrong. Please try again.') => {
        const list = res?.errors || res?.data?.errors;
        setErrors(Array.isArray(list) && list.length ? list : [res?.message || fallback]);
    };

    // ── Server actions ───────────────────────────────────────────────────────
    const startApplication = async (type, extra = {}) => {
        setBusy(true);
        try {
            const res = await startSignupPageApplicationService({
                type,
                solution,
                route,
                registry: { ...registry, confirmations: { ...registry.confirmations, terms } },
                source_page: handover?.source_page || '/signup',
                ...extra,
            });
            const d = res?.data;
            if (!d?.reference_no) { fail(res); return null; }
            saveToken(d.reference_no, d.access_token);
            clearHandover();
            const next = { ...d.application, token: d.access_token };
            setApp(next);
            return next;
        } catch (e) {
            fail(e?.response?.data || e);
            return null;
        } finally {
            setBusy(false);
        }
    };

    const loadQuote = async () => {
        setBusy(true);
        try {
            const res = await quoteSignupOrderService(solution, deployment);
            if (!res?.data) { fail(res); return false; }
            setQuote(res.data);
            return true;
        } catch (e) {
            fail(e?.response?.data || e);
            return false;
        } finally {
            setBusy(false);
        }
    };

    const addDocument = async (fields, file) => {
        setBusy(true);
        try {
            const res = await uploadSignupDocumentService(app.reference_no, app.token, fields, file);
            if (!res?.data?.application) { fail(res); return false; }
            setApp((a) => ({ ...a, ...res.data.application }));
            return true;
        } catch (e) {
            fail(e?.response?.data || e);
            return false;
        } finally {
            setBusy(false);
        }
    };

    /*
     * What the saved order was made from. Go back stays open on the order page after
     * Proceed to Payment, so anything changed since is sent as a revision of the same
     * order rather than left behind or started as a second one.
     */
    const savedOrderRef = useRef('');
    const orderKey = () => JSON.stringify({ solution, deployment, registry, terms });

    const openPayment = async () => {
        setBusy(true);
        try {
            let current = app;
            if (current && current.type === 'direct_order' && savedOrderRef.current !== orderKey()) {
                const res = await reviseSignupOrderService(current.reference_no, current.token, {
                    solution, deployment, registry: { ...registry, confirmations: { ...registry.confirmations, terms } },
                });
                const view = res?.data?.application;
                if (!view) { fail(res); return; }
                current = { ...current, ...view };
                setApp(current);
                savedOrderRef.current = orderKey();
            }
            if (!current) {
                current = await startApplication('direct_order', { deployment });
                if (!current) return;
                savedOrderRef.current = orderKey();
            }
            const res = await startSignupCardPaymentService(current.reference_no, current.token);
            if (!res?.data?.client_secret) { fail(res); return; }
            setPayment(res.data);
            setPaymentOpen(true);
        } catch (e) {
            fail(e?.response?.data || e);
        } finally {
            setBusy(false);
        }
    };

    const afterCardPaid = async (sessionId) => {
        setPaymentOpen(false);
        setStep('PAYMENT_PROCESSING');
        const res = await getSignupPaymentStatusService(app.reference_no, app.token, sessionId).catch(() => null);
        const view = res?.data?.application;
        if (view) setApp((a) => ({ ...a, ...view }));
        setStep(view && ['paid', 'fulfilled'].includes(view.status) ? 'PAID' : 'PAYMENT_PROCESSING');
    };

    const resend = async () => {
        setBusy(true);
        try { await resendSignupActivationService(app.reference_no, app.token); } catch (_) { /* invokeApi shows the reason */ } finally { setBusy(false); }
    };

    // ── Steps ────────────────────────────────────────────────────────────────
    const renderStep = () => {
        if (!catalog) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: ORANGE }}/></Box>;
        switch (step) {
            // ─── Direct ───────────────────────────────────────────────────────
            case 'DS1':
                return (
                    <>
                        <Heading value={TEXT.DS1}/>
                        {/* Orderable solutions only: COS Government and #DQEveryChild are not
                            ordered online, and stay with the chat. */}
                        <SelectOne options={catalog.SOLUTIONS.filter((s) => s.direct === 'order')} value={solution}
                            onChange={(v) => { setSolution(v); setDeployment({}); setQuote(null); }}/>
                        <StepActions onBack={back} nextDisabled={!solution} onNext={() => go('DS3')}/>
                    </>
                );
            case 'DS3':
                return (
                    <>
                        <Heading value={solution === 'P3' ? TEXT.DS3_6 : TEXT.DS3}/>
                        <DeploymentForm catalog={catalog} solution={solution} value={deployment} onChange={setDeployment}/>
                        <ErrorList errors={errors}/>
                        <StepActions onBack={back} busy={busy} onNext={async () => {
                            if (await loadQuote()) go('DS4');
                        }}/>
                    </>
                );
            case 'DS4':
                return (
                    <>
                        <Heading value={TEXT.DS4}/>
                        <RegistryForm catalog={catalog} value={registry} onChange={setRegistry} stateByCountry/>
                        <ErrorList errors={errors}/>
                        <StepActions onBack={back} busy={busy} onNext={async () => {
                            // Checked here, on this page — the confirmations included —
                            // rather than when the order is placed on the next one.
                            setBusy(true);
                            try {
                                const res = await validateSignupDetailsService(registry, false, true);
                                const list = res?.data?.errors;
                                if (!Array.isArray(list)) { fail(res); return; }
                                if (list.length) { setErrors(list); return; }
                            } catch (e) { fail(e?.response?.data || e); return; } finally { setBusy(false); }
                            if (quote || await loadQuote()) go('DS5');
                        }}/>
                    </>
                );
            case 'DS5':
                return (
                    <>
                        <Heading value={TEXT.DS5}/>
                        {quote && <OrderReview quote={quote} organization={orgName} organizationType={registry.organization.type} solutionLabel={solutionLabel} deployment={deployment} catalog={catalog}/>}
                        <Section>
                            <CheckLine checked={terms} onChange={setTerms}>
                                I agree to the Powered by DQ <a href={LEGAL_TERMS_URL} target="_blank" rel="noopener noreferrer" style={{ color: ORANGE }}>Terms of Service</a>.*
                            </CheckLine>
                        </Section>
                        <ErrorList errors={errors}/>
                        <StepActions onBack={back} busy={busy} nextDisabled={!terms || !quote || !(quote.total_usd > 0)}
                            nextLabel="Proceed to Payment" onNext={openPayment}/>
                    </>
                );

            // ─── Payment ──────────────────────────────────────────────────────
            case 'LOADING':
                return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: ORANGE }}/></Box>;
            case 'PAY':
                return (
                    <>
                        <Heading value={app?.type === 'dq_pro_applicant'
                            ? `Welcome back, ${firstName}. Your DQ Pro partnership for ${solutionLabel} is ready for payment.`
                            : TEXT.DS5}/>
                        {app?.quote && <OrderReview quote={app.quote} organization={orgName} solutionLabel={solutionLabel} deployment={app.deployment} catalog={catalog}/>}
                        <ErrorList errors={errors}/>
                        <StepActions busy={busy} nextLabel="Proceed to Payment" onNext={openPayment}/>
                    </>
                );
            case 'LINK_INVALID':
                return (
                    <>
                        <Typography sx={{ fontSize: '0.92rem' }}>This link has expired or is not valid on this device. Please use the latest email from DQ, or start again.</Typography>
                        <StepActions nextLabel="Start again" onNext={toChat}/>
                    </>
                );
            case 'PAYMENT_PROCESSING':
                return (
                    <Box>
                        <Typography sx={{ fontSize: '0.92rem' }}>Your payment is being confirmed. We’ll email {email} as soon as your workspace is ready.</Typography>
                        {app && (
                            <StepActions nextLabel="Check again" busy={busy} onNext={async () => {
                                const res = await getSignupPaymentStatusService(app.reference_no, app.token).catch(() => null);
                                const view = res?.data?.application;
                                if (view) { setApp((a) => ({ ...a, ...view })); if (['paid', 'fulfilled'].includes(view.status)) setStep('PAID'); }
                            }}/>
                        )}
                    </Box>
                );
            case 'PAID': {
                const partner = app?.type === 'dq_pro_applicant';
                const t = app?.workspace_ready ? (partner ? TEXT.DP11 : TEXT.DS7) : (partner ? TEXT.DP11_PENDING : TEXT.DS7_PENDING);
                return (
                    <>
                        <Done title={t.title(firstName)} lines={t.body(solutionLabel, email)}/>
                        {/* The activation email carries their temporary password. */}
                        <StepActions busy={busy} nextLabel="Log in" nextDisabled={!app?.workspace_ready}
                            onNext={() => history.push('/login')}
                            extra={(
                                <Button onClick={resend} disabled={busy || !app?.workspace_ready}
                                    sx={{ textTransform: 'none', color: ORANGE, fontWeight: 700 }}>
                                    Resend Activation Email
                                </Button>
                            )}/>
                    </>
                );
            }
            case 'WIRE_DONE':
                return (
                    <>
                        <Typography sx={{ fontSize: '0.92rem' }}>{TEXT.WIRE_SUBMITTED(email)}</Typography>
                        <StepActions backLabel="Close" onBack={toHome}/>
                    </>
                );

            // ─── DQ Pro ───────────────────────────────────────────────────────
            case 'GOV_NOTICE':
                return (
                    <>
                        <Typography sx={{ fontWeight: 800, mb: 1 }}>{TEXT.GOV_NOTICE.title}</Typography>
                        <Paragraphs lines={TEXT.GOV_NOTICE.paragraphs}/>
                        <ul style={{ marginTop: 0 }}>{TEXT.GOV_NOTICE.list.map((l) => <li key={l}>{l}</li>)}</ul>
                        <Paragraphs lines={[TEXT.GOV_NOTICE.after, TEXT.GOV_NOTICE.evidenceTitle]}/>
                        <ul style={{ marginTop: 0, marginBottom: 0 }}>{TEXT.GOV_NOTICE.evidence.map((l) => <li key={l}>{l}</li>)}</ul>
                        <StepActions onBack={back} onNext={() => go('DP6')}/>
                    </>
                );
            case 'DP6': {
                const govPath = solution === 'P0_GOV';
                return (
                    <>
                        <Heading value={TEXT.DP6}/>
                        {!govPath && <Typography sx={{ fontSize: '0.85rem', color: MUTED, mb: 1 }}>{TEXT.DP6_1_NOTE}</Typography>}
                        <RegistryForm catalog={catalog} value={registry} onChange={setRegistry} legalEntity stateByCountry/>
                        {!govPath && <Typography sx={{ mt: 2, fontSize: '0.85rem', color: MUTED }}>{TEXT.DP6_1_NEXT}</Typography>}
                        <ErrorList errors={errors}/>
                        <StepActions onBack={backIfNoApp} busy={busy} onNext={async () => {
                            if (app) { go(govPath ? 'DOCS' : 'DP7'); return; }
                            const created = await startApplication(govPath ? 'gov_verification' : 'dq_pro_applicant');
                            if (created) go(govPath ? 'DOCS' : 'DP7');
                        }}/>
                    </>
                );
            }
            case 'DP7':
                return (
                    <>
                        <Heading value={TEXT.DP7.message}/>
                        <SelectOne label={TEXT.DP7.label} help={TEXT.DP7.help} options={catalog.INTENDED_USE} value={intendedUse} onChange={setIntendedUse}/>
                        <ErrorList errors={errors}/>
                        <StepActions onBack={back} busy={busy} nextDisabled={!intendedUse} onNext={() => go(intendedUse === 'own' ? 'DP7_1' : 'DP8')}/>
                    </>
                );
            case 'DP7_1':
                return (
                    <>
                        <Typography sx={{ fontWeight: 800, mb: 1 }}>{TEXT.DP7_1.title}</Typography>
                        <Paragraphs lines={TEXT.DP7_1.body}/>
                        <ErrorList errors={errors}/>
                        <StepActions onBack={back} busy={busy} nextLabel="Confirm" onNext={async () => {
                            setBusy(true);
                            try {
                                const res = await saveSignupScreeningService(app.reference_no, app.token, 'own');
                                if (!res?.data) { fail(res); return; }
                                // As in the chat: the DQ Pro record closes and the direct route
                                // starts — here, on this page. The details already given stay.
                                setApp(null); setRoute('direct'); setSolution(''); setDeployment({}); setQuote(null); setTerms(false);
                                setStack([]); setErrors([]); setStep('DS1');
                            } catch (e) { fail(e?.response?.data || e); } finally { setBusy(false); }
                        }}/>
                    </>
                );
            case 'DP8':
                return (
                    <>
                        <Heading value={TEXT.DP8.message}/>
                        <ChecklistForm catalog={catalog} value={checklist} onChange={setChecklist}/>
                        <ErrorList errors={errors}/>
                        <StepActions onBack={back} busy={busy} nextLabel="Submit" onNext={async () => {
                            setBusy(true);
                            try {
                                const s1 = await saveSignupScreeningService(app.reference_no, app.token, intendedUse);
                                if (!s1?.data) { fail(s1); return; }
                                const res = await submitSignupChecklistService(app.reference_no, app.token, checklist);
                                if (!res?.data) { fail(res); return; }
                                setApp((a) => ({ ...a, ...res.data.application }));
                                setStack([]); setStep('DP9');
                            } catch (e) { fail(e?.response?.data || e); } finally { setBusy(false); }
                        }}/>
                    </>
                );
            case 'DP9':
                return (
                    <>
                        <Done title={TEXT.DP9.title} lines={TEXT.DP9.body(orgName, email)}/>
                        <StepActions backLabel="Close" onBack={toHome} nextLabel="View other solutions" onNext={toChat}/>
                    </>
                );

            // ─── COS Government ───────────────────────────────────────────────
            case 'DOCS':
                return (
                    <>
                        <Heading value={TEXT.DOCUMENTS(firstName)}/>
                        <DocumentsForm catalog={catalog} withType documents={app?.documents || []} onAdd={addDocument} busy={busy}/>
                        <NoteField label={TEXT.DOCUMENT_NOTE} value={docNote} onChange={setDocNote}/>
                        <ErrorList errors={errors}/>
                        <StepActions onBack={back} busy={busy} nextLabel="Continue" nextDisabled={!(app?.documents || []).length} onNext={async () => {
                            setBusy(true);
                            try {
                                const res = await submitSignupVerificationService(app.reference_no, app.token, docNote);
                                if (!res?.data) { fail(res); return; }
                                setApp((a) => ({ ...a, ...res.data.application }));
                                setStack([]); setStep('GOV_DONE');
                            } catch (e) { fail(e?.response?.data || e); } finally { setBusy(false); }
                        }}/>
                    </>
                );
            case 'GOV_DONE':
                return (
                    <>
                        <Done title={TEXT.GOV_SUBMITTED.title} lines={TEXT.GOV_SUBMITTED.body(firstName, orgName, email)}/>
                        <StepActions backLabel="Close" onBack={toHome} nextLabel="View other solutions" onNext={toChat}/>
                    </>
                );

            // ─── Opened directly: an account request ──────────────────────────
            case 'REQUEST':
                return (
                    <>
                        <Heading value={TEXT.DS4}/>
                        <RegistryForm catalog={catalog} value={registry} onChange={setRegistry} stateByCountry/>
                        <ErrorList errors={errors}/>
                        <StepActions onBack={back} busy={busy} nextLabel="Submit" onNext={async () => {
                            const created = await startApplication('account_request');
                            if (created) { setStack([]); setStep('REQUEST_DONE'); }
                        }}/>
                    </>
                );
            case 'REQUEST_DONE':
                return (
                    <>
                        <Done title="Thank you. We’ve received your sign-up request." lines={[
                            `Your reference is ${app?.reference_no}.`,
                            `We’ll review it and email ${email} your sign-in details once your workspace is ready.`,
                        ]}/>
                        <StepActions backLabel="Close" onBack={toHome}/>
                    </>
                );
            default:
                return null;
        }
    };

    const directReceive = (() => {
        const q = app?.quote || quote;
        if (!q || app?.type === 'dq_pro_applicant') return null;
        const credits = (q.credits_included || 0) + (q.credits_purchased || 0);
        return [q.licences ? `${q.licences.toLocaleString('en-US')} licenses` : null, credits ? `${credits.toLocaleString('en-US')} credits` : null].filter(Boolean).join(' and ') || null;
    })();

    return (
        <Box sx={{ width: '100%', textAlign: 'left' }}>
            {renderStep()}
            {app && payment && (
                <DqtCheckoutDialog
                    open={paymentOpen}
                    publishableKey={catalog?.stripe_publishable_key}
                    clientSecret={payment.client_secret}
                    item={payment.item}
                    walletsEnabled={payment.wallets_enabled}
                    paymentOptions={catalog?.payment_options}
                    receiveLabel={app.type === 'dq_pro_applicant' ? 'DQ Tokens' : 'licenses and credits'}
                    receiveText={directReceive}
                    fallbackEmail={email}
                    onClose={() => setPaymentOpen(false)}
                    onPaid={afterCardPaid}
                    createWire={() => startSignupWirePaymentService(app.reference_no, app.token)}
                    uploadProof={(_orderId, file, note) => uploadSignupWireProofService(app.reference_no, app.token, file, note)}
                    onWireSubmitted={() => { setPaymentOpen(false); setStep('WIRE_DONE'); }}
                />
            )}
        </Box>
    );
};

export default SignupApplicationSteps;
